import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Chip,
  CircularProgress, Alert, LinearProgress,
} from '@mui/material';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNewIcon     from '@mui/icons-material/OpenInNew';
import DashboardIcon     from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon      from '@mui/icons-material/Security';
import SpeedIcon         from '@mui/icons-material/Speed';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import CelebrationIcon  from '@mui/icons-material/Celebration';
import api               from '../../services/api';

const C = {
  purple:     '#552583',
  purpleDark: '#33154f',
  gold:       '#FDB927',
  bg:         '#E4F0F6',
  white:      '#ffffff',
  green:      '#22c55e',
  orange:     '#f59e0b',
};

const steps = [
  { label: 'Informations personnelles',  desc: 'Nom, adresse, date de naissance' },
  { label: 'Informations bancaires',     desc: 'IBAN pour recevoir vos versements' },
  { label: 'Vérification d\'identité',   desc: 'Pièce d\'identité ou passeport' },
  { label: 'Compte activé',              desc: 'Prêt à recevoir des paiements' },
];

export default function StripeOnboarding() {
  const navigate       = useNavigate();
  const [params]       = useSearchParams();
  const isReturn       = params.get('return') === 'true';
  const isRefresh      = params.get('refresh') === 'true';

  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [btnLoad,  setBtnLoad]  = useState(false);
  const [error,    setError]    = useState('');

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/connect/status');
      setStatus(data);
    } catch {
      setError('Impossible de charger le statut Stripe.');
    } finally {
      setLoading(false);
    }
  };

  const handleOnboard = async () => {
    try {
      setBtnLoad(true);
      const { data } = await api.post('/connect/onboard');
      window.location.href = data.url;
    } catch {
      setError('Erreur lors de la connexion à Stripe.');
      setBtnLoad(false);
    }
  };

  const handleDashboard = async () => {
    try {
      setBtnLoad(true);
      const { data } = await api.get('/connect/dashboard-link');
      window.open(data.url, '_blank');
    } catch {
      setError('Impossible d\'ouvrir le tableau de bord Stripe.');
    } finally {
      setBtnLoad(false);
    }
  };

  const completedSteps = !status?.connected ? 0
    : !status?.onboarding_done ? 1
    : !status?.charges_enabled ? 2
    : !status?.payouts_enabled ? 3
    : 4;

  const progressPct = (completedSteps / 4) * 100;

  return (
    <Box sx={{ background: C.bg, minHeight: '100vh' }}>

      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 65%, #7a3aad 100%)`,
        color: C.white, py: { xs: 6, md: 9 }, px: 2, textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, borderRadius: '50%', background: 'rgba(253,185,39,0.07)', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '20px', background: 'rgba(253,185,39,0.15)', border: '1.5px solid rgba(253,185,39,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AccountBalanceIcon sx={{ fontSize: 38, color: C.gold }} />
          </Box>
        </Box>
        <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
          Recevez vos paiements
        </Typography>
        <Typography sx={{ opacity: 0.75, maxWidth: 480, mx: 'auto', mb: 3 }}>
          Connectez votre compte Stripe pour recevoir automatiquement vos revenus chaque semaine
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip icon={<SecurityIcon sx={{ fontSize: 16 }} />} label="Sécurisé par Stripe" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
          <Chip icon={<SpeedIcon sx={{ fontSize: 16 }} />} label="Virements hebdomadaires" sx={{ bgcolor: 'rgba(253,185,39,0.18)', color: C.gold, fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(253,185,39,0.3)' }} />
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: C.purple }} />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

            {isReturn && status?.charges_enabled && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}>
                <CelebrationIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} /> Votre compte Stripe est activé ! Vous pouvez maintenant recevoir des paiements.
              </Alert>
            )}

            {isRefresh && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '12px' }}>
                Le lien d'onboarding a expiré. Cliquez à nouveau pour continuer.
              </Alert>
            )}

            {/* Progression */}
            <Paper elevation={0} sx={{ p: 3.5, mb: 3, borderRadius: '20px', background: C.white, border: '1px solid rgba(85,37,131,0.08)', boxShadow: '0 4px 20px rgba(85,37,131,0.06)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography fontWeight={700} sx={{ color: C.purple }}>Progression du compte</Typography>
                <Chip
                  label={
                    completedSteps === 4 ? 'Compte actif' :
                    completedSteps === 0 ? 'Non démarré' :
                    'En cours…'
                  }
                  sx={{
                    bgcolor: completedSteps === 4 ? 'rgba(34,197,94,0.1)' : completedSteps === 0 ? 'rgba(85,37,131,0.08)' : 'rgba(245,158,11,0.1)',
                    color:   completedSteps === 4 ? C.green : completedSteps === 0 ? C.purple : C.orange,
                    fontWeight: 700, fontSize: '0.78rem',
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  height: 8, borderRadius: 4, mb: 3,
                  bgcolor: 'rgba(85,37,131,0.08)',
                  '& .MuiLinearProgress-bar': {
                    background: completedSteps === 4
                      ? `linear-gradient(90deg, ${C.green}, #16a34a)`
                      : `linear-gradient(90deg, ${C.purple}, ${C.gold})`,
                    borderRadius: 4,
                  },
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {steps.map((step, i) => {
                  const done = i < completedSteps;
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {done
                        ? <CheckCircleIcon sx={{ color: C.green, fontSize: 22, flexShrink: 0 }} />
                        : <RadioButtonUncheckedIcon sx={{ color: 'rgba(85,37,131,0.25)', fontSize: 22, flexShrink: 0 }} />
                      }
                      <Box>
                        <Typography variant="body2" fontWeight={done ? 700 : 500} sx={{ color: done ? C.purple : 'rgba(0,0,0,0.45)' }}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.38)' }}>
                          {step.desc}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Taux de commission */}
            <Paper elevation={0} sx={{ p: 3.5, mb: 3, borderRadius: '20px', background: C.white, border: '1px solid rgba(85,37,131,0.08)', boxShadow: '0 4px 20px rgba(85,37,131,0.06)' }}>
              <Typography fontWeight={700} sx={{ color: C.purple, mb: 2 }}>Taux de commission Usafiri</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {[
                  { label: 'Restauration', rate: '15%' },
                  { label: 'VTC',          rate: '15%' },
                  { label: 'Location',     rate: '12%' },
                  { label: 'Covoiturage',  rate: '10%' },
                  { label: 'Logistique',   rate: '10%' },
                  { label: 'Courses',      rate: '12%' },
                ].map((s) => (
                  <Box key={s.label} sx={{ p: 2, borderRadius: '12px', background: 'rgba(85,37,131,0.04)', border: '1px solid rgba(85,37,131,0.08)', textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: C.purple }}>{s.rate}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)' }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.4)', display: 'block', mt: 2 }}>
                La commission est déduite automatiquement à chaque paiement. Vous recevez le reste chaque lundi.
              </Typography>
            </Paper>

            {/* CTA */}
            <Paper elevation={0} sx={{ p: 3.5, borderRadius: '20px', background: C.white, border: '1px solid rgba(85,37,131,0.08)', boxShadow: '0 4px 20px rgba(85,37,131,0.06)' }}>
              {completedSteps < 4 ? (
                <>
                  <Typography fontWeight={700} sx={{ color: C.purple, mb: 1 }}>
                    {completedSteps === 0 ? 'Connecter mon compte Stripe' : 'Finaliser mon compte Stripe'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)', mb: 3 }}>
                    Vous serez redirigé vers Stripe pour compléter votre inscription en toute sécurité.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={btnLoad ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <ArrowForwardIcon />}
                    onClick={handleOnboard}
                    disabled={btnLoad}
                    sx={{
                      py: 1.8, borderRadius: '14px', fontWeight: 800, fontSize: '1rem',
                      background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`,
                      boxShadow: '0 8px 30px rgba(85,37,131,0.3)',
                      textTransform: 'none',
                      '&:hover': { background: `linear-gradient(135deg, ${C.purpleDark}, #c98a00)` },
                    }}
                  >
                    {completedSteps === 0 ? 'Commencer l\'inscription Stripe' : 'Continuer l\'inscription'}
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}>
                    Votre compte est actif. Vous recevrez vos virements chaque lundi.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<DashboardIcon />}
                      endIcon={btnLoad ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
                      onClick={handleDashboard}
                      disabled={btnLoad}
                      sx={{
                        flex: 1, py: 1.5, borderRadius: '12px', fontWeight: 700,
                        background: `linear-gradient(135deg, ${C.purple}, ${C.gold})`,
                        textTransform: 'none',
                        '&:hover': { background: `linear-gradient(135deg, ${C.purpleDark}, #c98a00)` },
                      }}
                    >
                      Mon tableau de bord Stripe
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/partner/dashboard')}
                      sx={{ flex: 1, py: 1.5, borderRadius: '12px', fontWeight: 700, textTransform: 'none', borderColor: 'rgba(85,37,131,0.3)', color: C.purple, '&:hover': { borderColor: C.purple, background: 'rgba(85,37,131,0.04)' } }}
                    >
                      Mon espace partenaire
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
}
